/**
 * CelLook — the one visual vocabulary every combat effect is drawn in.
 *
 * Effects here are cartoon OBJECTS, not light. A shape is an opaque-looking,
 * saturated body in the character's colour with a white-hot core and a dark
 * INK rim, banded in hard cel steps, and it dies by EROSION — a noise field
 * eats it from the edges with a bright band on the eating front — never by a
 * uniform opacity fade. That is what reads on the bright teal deck with bloom
 * off (every phone), where pass 1's additive glow simply vanished.
 *
 * Everything is TSL so it costs nothing per instance: the noise is a two-tap
 * value noise on the shape's own UV, seeded per instance so two puffs never
 * erode identically. No textures, no per-effect materials.
 */

import { float, vec2, vec3, fract, sin, dot, floor, mix, smoothstep, uniform } from 'three/tsl';

/** Cheap 2D hash: fract(sin(dot)) — enough for erosion, never for anything periodic. */
export function hash21(p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))).mul(43758.5453));
}

/** Bilinear value noise in 0..1 on a 2D point. Two taps of this make a cloud. */
export function vnoise(p) {
  const i = floor(p);
  const f = fract(p);
  const u = f.mul(f).mul(float(3).sub(f.mul(2)));
  const a = hash21(i);
  const b = hash21(i.add(vec2(1, 0)));
  const c = hash21(i.add(vec2(0, 1)));
  const d = hash21(i.add(vec2(1, 1)));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

/** Two octaves: the blotchy field a stylised dissolve wants. `seed` shifts it per instance. */
export function cloud(p, seed) {
  const q = p.add(seed);
  return vnoise(q).mul(0.64).add(vnoise(q.mul(2.7).add(3.1)).mul(0.36));
}

/**
 * Erosion. `progress` 0 → 1 kills the shape from the noise's low values up:
 * at 0 everything survives, at 1 nothing does. Returns the surviving mask
 * (a tight anti-aliased edge, so it stays a CUT, not a fade) and the FRONT —
 * a thin band just above the threshold that the caller brightens, which is
 * the burning-paper edge that makes a dissolve read as an event.
 */
export function erode(noise, progress, { edge = 0.05, front = 0.16 } = {}) {
  // Threshold runs from -edge to 1 + front so progress 1 clears every texel.
  const th = mix(float(-edge), float(1).add(front), progress);
  const d = noise.sub(th);
  const alive = smoothstep(float(0), float(edge), d);
  const band = smoothstep(float(0), float(0.02), d).mul(smoothstep(float(front + 0.03), float(front), d));
  // No band while the shape is whole: it appears the moment the erosion starts.
  return { alive, front: band.mul(smoothstep(float(0), float(0.03), progress)) };
}

/**
 * The palette every shape is coloured from, off one tint uniform.
 *
 *   core  white-hot heart (× coreGain so it blooms on the tiers that bloom)
 *   hot   the bright inner band
 *   body  the identity colour itself
 *   deep  the shaded side of a puff or a chunk
 *   ink   the outline: near-black, faintly tinted so it is never dead grey
 */
export function palette(tint, gain = coreGain) {
  return {
    // The core is a WARM white: a hot thing, not a paper cut-out.
    core: mix(tint, vec3(1, 0.98, 0.93), 0.93).mul(gain),
    hot: mix(tint, vec3(1, 1, 1), 0.38),
    body: tint,
    deep: tint.mul(0.6),
    ink: mix(tint, vec3(0.02, 0.02, 0.05), 0.86),
  };
}

const _hsl = { h: 0, s: 0, l: 0 };

/**
 * A body colour with (HSL) saturation under this has no hue to speak of, and
 * gets the steel accent instead of a saturated version of its noise.
 */
const NEUTRAL_S = 0.15;
/** The accent for a hueless identity: steel blue. AAPL's graphite is the case. */
const STEEL_H = 204 / 360;
const STEEL_S = 0.52;
const STEEL_L = 0.44;

/**
 * The VIVID form of a character colour, for everything the kit draws: the
 * hue kept, the saturation pushed, the lightness clamped into the band where
 * a body colour reads as paint next to a white core and an ink rim. SPCX's
 * pale amber becomes a real orange; NVDA's dark green a real green. AAPL's
 * graphite has no hue, so pushing its saturation only made it a paler white:
 * it takes a deliberate STEEL BLUE accent instead, so its stars, swings and
 * shards are a colour and not a blank. Written into `out` and returned.
 */
export function identity(hex, out) {
  out.set(hex);
  out.getHSL(_hsl);
  if (_hsl.s < NEUTRAL_S) {
    out.setHSL(STEEL_H, STEEL_S, STEEL_L);
    return out;
  }
  const s = Math.min(1, _hsl.s * 1.3 + 0.12);
  const l = Math.max(0.42, Math.min(0.56, _hsl.l));
  out.setHSL(_hsl.h, s, l);
  return out;
}

/**
 * A FLOOR colour (`telegraphHex`) for the kit: kept as authored, only
 * normalised so its brightest channel is 1. Floor colours are chosen for the
 * deck and must not be pushed the way identities are — AAPL's cream would
 * come out chartreuse.
 */
export function floorTint(hex, out) {
  out.set(hex);
  const m = Math.max(out.r, out.g, out.b, 1e-4);
  out.multiplyScalar(1 / m);
  return out;
}

/**
 * How bright a white core is. 1 on the tiers without bloom (the core is plain
 * white); above the bloom threshold (2.0 in linear HDR) on the ones with it, so
 * ONLY the small hot cores bloom — the bodies and rims stay flat cartoon paint.
 * A shared uniform: one write on a tier change, no recompile.
 */
export const coreGain = uniform(1);

export function setCoreGain(v) {
  coreGain.value = v;
}

/** The overshoot pop: biggest on frame one, settling to 1 by `popT` of the life. */
export function popScale(t, popT = 0.22, over = 0.3) {
  const k = Math.min(1, t / popT);
  return 1 + over * (1 - k) * (1 - k);
}

/** Erosion progress from a life fraction: whole until `from`, gone at 1. */
export function erosionAt(t, from = 0.35) {
  if (t <= from) return 0;
  const k = (t - from) / (1 - from);
  return Math.min(1, k * k * (3 - 2 * k));
}
